import React, { useEffect } from 'react';
import {
  Modal, 
  Box, 
  Typography, 
  IconButton, 
  List, 
  ListItem, 
  Avatar, 
  useTheme,
  CircularProgress, 
  Alert, 
  Stack, 
  Paper,
  Button, // Import Button
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';
import { gql, useQuery } from '@apollo/client';


const YOUR_TEXTURE_SVG_PATH = '/textures/Texture.svg';
const YOUR_GRADIENT_SVG_PATH = '/images/Gradient_Popup.svg';

// GRAPHQL QUERY
const GET_TENANT_ACTIVITY_LOGS = gql`
  query GetTenantActivityLogs($tenantId: ID!, $limit: Int) {
    tenantActivityLogs(tenantId: $tenantId, limit: $limit) {
      id
      activityType
      createdAt
      performedByUser {
        id
        name
        photo 
      }
      targetUser {
        id
        name
        email
        photo
      }
      details
    }
  }
`;

// --- Reusable Error Display Component ---
const ErrorDisplay = ({ onRefresh }: { onRefresh: () => void; }) => (
  <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    p: 3,
    height: '100%',
  }}>
    <img src="/usmerror.svg" alt="Error" style={{ width: '130px', marginBottom: '16px' }} />
    <Typography variant="h6" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
      Unable to Display Data
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '300px', mb: 2 }}>
      We're having trouble loading activities right now. Please try again.
    </Typography>
    <Button
        variant="text"
        onClick={onRefresh}
        sx={{
            textTransform: 'none',
            color: '#0064FF',
            fontWeight: 500,
            fontFamily:'Inter',
            lineHeight:'21px',
            fontSize:'14px',
            mb: 15
        }}
        >
        Refresh Page
        </Button>
    {/* <Button
      variant="contained"
      onClick={onRefresh}
      sx={{
        textTransform: 'none',
        borderRadius: '8px',
        backgroundColor: '#0064FF', // A standard blue color
         '&:hover': { backgroundColor: '#0052cc' }
      }}
    >
      Refresh
    </Button> */}
  </Box>
);

// --- Component Props ---
interface ActivityLogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string | null;
}

// --- Helper Functions and Color Mappings ---

const roleDotColors: { [key: string]: string } = {
  Editor: '#0064FF',
  Viewer: '#6C737F',
  Admin: '#0064FF',
};

const formatLogDate = (isoString: string): string => {
  if (!isoString) return 'Invalid Date';
  return new Date(isoString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).toUpperCase();
};

const formatLogTime = (isoString: string): string => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const groupLogsByDate = (logs: any[]): Record<string, any[]> => {
  if (!logs) return {};
  return logs.reduce((acc, log) => {
    const dateKey = formatLogDate(log.createdAt);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(log);
    return acc;
  }, {} as Record<string, any[]>);
};


// --- Helper Components for Rendering ---

const RoleDisplay: React.FC<{ role: string }> = ({ role }) => {
    const capitalizedRole = role ? role.charAt(0).toUpperCase() + role.slice(1).toLowerCase() : 'N/A';
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: roleDotColors[capitalizedRole] || '#6C737F', mr: 1 }} />
            <Typography variant="body2" sx={{ color: '#344054', fontWeight: 400, fontSize: '14px' }}>{capitalizedRole}</Typography>
        </Box>
    );
};

const ActivityIcon: React.FC<{ activityType: string }> = ({ activityType }) => {
  let iconSrc = '/icons/edit-al.svg'; // Default icon

   if (activityType.includes('ADD') || activityType.includes('INVITE')) {
    iconSrc = '/icons/add-al.svg';
  } else if (activityType.includes('REMOVED')) {
    iconSrc = '/icons/delete-al.svg';
  } else if (activityType.includes('ROLE')) {
    iconSrc = '/icons/edit-al.svg';
  } else if (activityType.includes('ACCEPT')) {
    iconSrc = '/icons/email-al.svg';
  }

  return (
    <Box sx={{
        width: '25px',
        height: '25px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: '1px solid #EAECF0',
        mt: '2px', // Nudge down for alignment
        bgcolor: '#FFFFFF', // To hide line underneath
        position: 'relative', // For zIndex
        zIndex: 1,
    }}>
        <img src={iconSrc} alt={`${activityType} icon`} style={{ width: '14px', height: '14px' }} />
    </Box>
  );
};

const LogDetailContent: React.FC<{ log: any }> = ({ log }) => {
    const { activityType, targetUser, details } = log;

    const typesWithDetailBox = [
        'USER_ADDED_TO_TENANT',
        'USER_REMOVED_FROM_TENANT',
        'USER_ROLE_IN_TENANT_CHANGED',
        'USER_INVITED_TO_TENANT' 
    ];

    if (!typesWithDetailBox.includes(activityType)) {
        return null; 
    }

    const userToShow = {
        name: details?.deletedUserName || targetUser?.name,
        email: details?.deletedUserEmail || targetUser?.email,
        photo: details?.deletedUserPhoto || targetUser?.photo,
        role: details?.role || (activityType === 'USER_INVITED_TO_TENANT' ? details?.roleInTenant : 'Editor')
    };

    let barColor = '#F79009';
    if (activityType === 'USER_ADDED_TO_TENANT' || activityType === 'USER_INVITED_TO_TENANT') {
        barColor = '#0064FF';
    } else if (activityType === 'USER_REMOVED_FROM_TENANT') {
        barColor = '#D92D20';
    }
    
    return (
      <Box sx={{ 
        position: 'relative', 
        ml: '32px', 
        mt: 1,
        bgcolor: '#FAFAFA',
        borderRadius: '8px',
        p: 1.5,
        pl: '24px', 
      }}>
        <Box sx={{
            position: 'absolute',
            left: '8px', 
            top: 8, 
            bottom: 8, 
            width: '3px',
            bgcolor: barColor,
            borderRadius: '3px',
        }} />

        {(() => {
          switch (activityType) {
            case 'USER_ADDED_TO_TENANT':
            case 'USER_REMOVED_FROM_TENANT':
            case 'USER_INVITED_TO_TENANT':
              if (!userToShow.name) return null;
              return (
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar src={userToShow.photo} sx={{ width: 28, height: 28 }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#344054', fontSize: '12px' }}>{userToShow.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#667085', fontSize: '12px', fontWeight: 400 }}>{userToShow.email}</Typography>
                    </Box>
                  </Stack>
              );
            case 'USER_ROLE_IN_TENANT_CHANGED':
              return (
                <Stack direction="row" spacing={1} alignItems="center">
                  <RoleDisplay role={details?.oldRole || 'Role'} />
                  <Typography sx={{ color: '#667085', fontSize: '14px' }}>»</Typography>
                  <RoleDisplay role={details?.newRole || 'Role'} />
                </Stack>
              );
            default:
              return null;
          }
        })()}
      </Box>
    );
};

// --- Main Component ---
const ActivityLog: React.FC<ActivityLogProps> = ({ open, onClose, tenantId }) => {
  const theme = useTheme();
  const { loading, error, data, refetch } = useQuery(GET_TENANT_ACTIVITY_LOGS, {
    variables: { tenantId: tenantId, limit: 10 },
    skip: !open || !tenantId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => { if (open && tenantId) refetch(); }, [open, tenantId, refetch]);

  const renderLogs = (fetchedLogs: any[] | undefined) => {
    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    
    // Use the reusable ErrorDisplay component
    if (error) return <ErrorDisplay onRefresh={() => refetch()} />;
    
    if (!fetchedLogs || fetchedLogs.length === 0) return <Typography sx={{ textAlign: 'center', p: 4, color: 'text.secondary' }}>No activities recorded.</Typography>;

    const groupedLogs = groupLogsByDate(fetchedLogs);

    return (
      <List disablePadding>
        {Object.entries(groupedLogs).map(([date, logs]) => (
          <React.Fragment key={date}>
            <ListItem sx={{ py: 1, px: 3, bgcolor: '#FAFAFA', borderTop: '1px solid #EAECF0', borderBottom: '1px solid #EAECF0' }}>
                <img src="/icons/calendar-al.svg" alt="date" style={{ width: '16px', height: '16px', marginRight: theme.spacing(1.5) }} />
                <Typography variant="body2" sx={{ fontWeight: 550, fontSize: '14px', color: '#344054' }}>
                    {date}
                </Typography>
            </ListItem>
            
            <Box sx={{ position: 'relative', px: 3, pt: 2, pb: 1 }}>
                <Box sx={{
                    position: 'absolute',
                    left: '35px', 
                    top: 0, 
                    bottom: 0,
                    width: '1.5px',
                    backgroundImage: `repeating-linear-gradient(
                        transparent 0,
                        transparent 8px,
                        ${theme.palette.grey[300]} 8px,
                        ${theme.palette.grey[300]} 16px
                    )`,
                }}/>
              {(logs as any[]).map((log) => (
                <ListItem key={log.id} sx={{ display: 'block', py: 1.5, px: 0, position: 'relative' }}>
                  <Stack direction="row" alignItems="flex-start" width="100%">
                    <ActivityIcon activityType={log.activityType} />
                    <Typography variant="body2" sx={{ flexGrow: 1, color: '#344054', ml: 1, fontSize: '14px', fontWeight: '400' }}>
                      {(() => {
                          const performer = <Typography component="span" sx={{ fontWeight: '400 !important', fontSize: '14px !important' }}>{log.performedByUser?.name || 'System'}</Typography>;
                          const target = <Typography component="span" sx={{ fontWeight: '400 !important', fontSize: '14px !important' }}>{log.targetUser?.name || 'a user'}</Typography>;
                          
                          switch(log.activityType) {
                              case 'USER_ADDED_TO_TENANT': 
                                return <>New user added by {performer}</>;
                              case 'USER_REMOVED_FROM_TENANT': 
                                return <>User deleted by {performer}</>;
                              case 'USER_ROLE_IN_TENANT_CHANGED': 
                                return <>Role changed for {target} by {performer}</>;
                              case 'USER_ACCEPTED_INVITATION': 
                                return <>Email Invitation accepted by {target}</>;
                              case 'USER_INVITED_TO_TENANT':
                                return <>{target} invited to tenant by {performer}</>;
                              default: 
                                return <>{log.activityType} by {performer}</>;
                          }
                      })()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#98A2B3', whiteSpace: 'nowrap', ml: 1 }}>
                      {formatLogTime(log.createdAt)}
                    </Typography>
                  </Stack>
                  <LogDetailContent log={log} />
                </ListItem>
              ))}
            </Box>
          </React.Fragment>
        ))}
      </List>
    );
  };
  
  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(0px)',
            backgroundColor: '#000000A6'
          }
        }
      }}
    >
      <Paper sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: { xs: '90%', sm: 480 },
          height: 'calc(100% - 32px)',
          borderRadius: '16px',
          boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
          bgcolor: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        <IconButton 
            onClick={onClose} 
            sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}
        >
            <CloseIcon />
        </IconButton>
        
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
            flexShrink: 0,
            backgroundImage: `url(${YOUR_TEXTURE_SVG_PATH}), url(${YOUR_GRADIENT_SVG_PATH})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: '8px',
            p: 1,
            minHeight: '120px',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
                <Box sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '10px',
                    backgroundColor: '#E5EFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <img src="/activity-lg.svg" alt="Activity Log" style={{ width: '28px', height: '28px' }} />
                </Box>
                <Typography variant="h6" sx={{
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: '18px',
                    color: '#333333',
                }}>
                    Activity Log
                </Typography>
            </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{
            mt: 0,
            fontWeight: 600,
            fontFamily: 'Inter',
            fontSize: '14px',
            color: '#475467',
            px: '24px',
            pt: '1px',
            pb: '20px'
          }}
        >
          Showing 10 recent activities
        </Typography>


        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {renderLogs(data?.tenantActivityLogs)}
        </Box>
      </Paper>
    </Modal>
  );
};

export default ActivityLog;